import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DatabaseZap, Loader2, CheckCircle2, RefreshCw, ArrowRightLeft } from "lucide-react";

export function CrmImportPanel({ caseId }) {
  const [connected, setConnected] = useState([]);
  const [provider, setProvider] = useState("");
  const [keepSynced, setKeepSynced] = useState(false);
  const [status, setStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try {
      const [intg, st] = await Promise.all([api.get("/attorney/integrations"), api.get(`/cases/${caseId}/crm-status`)]);
      const conns = intg.data.connectors.filter((c) => c.connection?.status === "connected");
      setConnected(conns);
      if (conns.length && !provider) setProvider(conns[0].key);
      setStatus(st.data);
      if (st.data?.sync) setKeepSynced(!!st.data.sync.keep_synced);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const doImport = async () => {
    if (!provider) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/crm-import`, { provider, keep_synced: keepSynced });
      setLastResult(data);
      toast.success(data.sandbox ? "Case imported (sandbox) — mapping & transfer logged" : "Case imported to CRM");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const toggleSync = async () => {
    const next = !keepSynced;
    setKeepSynced(next);
    try { await api.post(`/cases/${caseId}/crm-keep-synced`, { provider, keep_synced: next }); toast.success(next ? "Keep Synced enabled" : "Keep Synced disabled"); }
    catch (e) { setKeepSynced(!next); toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const alreadyImported = !!status?.sync;

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="crm-import-panel">
      <div className="flex items-center gap-2 mb-4">
        <DatabaseZap className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">Import Into My CRM</h3>
      </div>

      {connected.length === 0 ? (
        <div className="text-sm text-slate-500">
          No CRM connected yet. <Link to="/integrations" className="text-cobalt font-medium hover:underline">Open the Integration Center</Link> to connect one.
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="crm-provider-select"
              className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none">
              {connected.map((c) => <option key={c.key} value={c.key}>{c.name}{!c.live_supported ? " (sandbox)" : ""}</option>)}
            </select>
            <button onClick={doImport} disabled={importing} data-testid="crm-import-btn"
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <DatabaseZap className="w-4 h-4" />} {alreadyImported ? "Re-import" : "Import Into My CRM"}
            </button>
          </div>

          <label className="mt-4 flex items-center gap-2.5 cursor-pointer" data-testid="keep-synced-toggle">
            <input type="checkbox" checked={keepSynced} onChange={toggleSync} className="h-4 w-4 rounded border-slate-300 text-cobalt focus:ring-cobalt" />
            <span className="text-sm text-navy font-medium flex items-center gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5 text-cobalt" /> Keep Synced</span>
            <span className="text-xs text-slate-400">— auto-sync future evidence, documents, messages & timeline</span>
          </label>

          {alreadyImported && (
            <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5" data-testid="crm-last-import">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Last imported to <b className="text-navy">{status.sync.provider}</b> · {new Date(status.sync.last_import_at).toLocaleString()}
            </div>
          )}

          {lastResult?.job?.items && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              {lastResult.sandbox && <div className="mb-2 text-[11px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2.5 py-1 inline-block">SANDBOX — no external call made</div>}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {lastResult.job.items.map((it) => (
                  <div key={it.section} className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                    <div className="text-sm font-bold text-navy">{it.count}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wide">{it.section}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
